import React, { useRef, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Inputmask from "inputmask";

const INPUTMASK_CONFIG = {
    alias: "numeric",
    allowMinus: false,
    rightAlign: false,
    groupSeparator: ",",
    autoGroup: true,
    digits: 0,
    radixPoint: "",
    prefix: "$",
    positionCaretOnClick: "none",
    max: "999999999999999",
    onBeforePaste: function (pastedValue) {
        return pastedValue.replace(/[^\d]/g, "");
    },
    onBeforeWrite: function (event, buffer, caretPos, opts) {
        const { type } = event;
        if (type !== "checkval" || opts?.skip) {
            return;
        }
        const isLast3DigitsZero = buffer.slice(0, 3).every((item) => item === "0");
        const newCaretPos = buffer.length <= 4 ? 3 : 4;
        return { caret: isLast3DigitsZero && buffer.length >= 4 ? newCaretPos : caretPos };
    },
    onKeyDown: function (event, buffer, caretPos, opts) {
        opts.skip = event.key === "Backspace" && caretPos?.end === 0;
    },
};

const PriceRangeInput = ({ values = { priceMin: "", priceMax: "" }, onChange }) => {
    const priceMinRef = useRef();
    const priceMaxRef = useRef();
    const [dirtyMin, setDirtyMin] = useState(false);
    const [dirtyMax, setDirtyMax] = useState(false);
    const [touchedMin, setTouchedMin] = useState(false);
    const [touchedMax, setTouchedMax] = useState(false);
    const [focusedField, setFocusedField] = useState(null);

    useEffect(() => {
        const inputMask = Inputmask(INPUTMASK_CONFIG);
        inputMask.mask("feed-price-min");
        inputMask.mask("feed-price-max");
    }, []);

    const handlePriceInput = (val, setValue, isDirty, setDirty) => {
        const cleanValue = val?.replace(/[^\d]/g, "") || "";

        if (!cleanValue) {
            setValue("");
            setDirty(false);
            return;
        }

        // If the input is a single digit and not dirty, add three zeros
        if (cleanValue.length === 1 && !isDirty) {
            setValue(cleanValue + "000");
            setTimeout(() => {
                const ref = cleanValue === values.priceMin ? priceMinRef : priceMaxRef;
                if (ref && ref.current) {
                    ref.current.setSelectionRange(1, 1);
                }
            }, 0);
            return;
        }

        setValue(cleanValue);
        setDirty(true);
    };

    const handleFocus = (ref, value, setValue, setDirty, field) => {
        setFocusedField(field);
        const cleanValue = value?.replace(/[^\d]/g, "") || "";
        if (!cleanValue) {
            setValue("000");
            setDirty(false);
            setTimeout(() => {
                if (ref && ref.current) {
                    ref.current.setSelectionRange(0, 0);
                }
            }, 0);
        }
    };

    const handleBlur = (field) => {
        setFocusedField(null);
        if (field === "priceMin") {
            if (values.priceMin === "000") onChange("priceMin", "");
            setTouchedMin(true);
            setTouchedMax(true);
        } else {
            if (values.priceMax === "000") onChange("priceMax", "");
            setTouchedMax(true);
            setTouchedMin(true);
        }
    };

    const isInvalid =
        (touchedMin || touchedMax) &&
        focusedField === null &&
        Number(values.priceMax) > 0 &&
        Number(values.priceMin) > 0 &&
        Number(values.priceMax) < Number(values.priceMin);

    return (
        <div>
            <label className="tw-block tw-mb-1 tw-text-[14px] tw-text-[#555E67]">Price</label>
            <div className="tw-flex tw-gap-4">
                {/* Min */}
                <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center">
                    <TextField
                        inputRef={priceMinRef}
                        id="feed-price-min"
                        type="text"
                        value={values.priceMin || ""}
                        onChange={(e) =>
                            handlePriceInput(
                                e.target.value,
                                (v) => onChange("priceMin", v),
                                dirtyMin,
                                setDirtyMin
                            )
                        }
                        onFocus={() =>
                            handleFocus(
                                priceMinRef,
                                values.priceMin,
                                (v) => onChange("priceMin", v),
                                setDirtyMin,
                                "priceMin"
                            )
                        }
                        onClick={() =>
                            handleFocus(
                                priceMinRef,
                                values.priceMin,
                                (v) => onChange("priceMin", v),
                                setDirtyMin,
                                "priceMin"
                            )
                        }
                        onBlur={() => handleBlur("priceMin")}
                        placeholder="$0"
                        size="small"
                        fullWidth
                        error={isInvalid}
                        inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                        }}
                        InputProps={{
                            className: "!tw-px-2",
                            sx: {
                                borderRadius: 2,
                                textAlign: "center",
                                "&.Mui-error": {
                                    color: "red",
                                },
                            },
                            inputProps: {
                                style: {
                                    textAlign: "center",
                                    color: isInvalid ? "red" : "inherit",
                                },
                            },
                        }}
                    />
                    <span className="tw-mt-1 tw-text-xs tw-text-gray-400 tw-text-center">min</span>
                </div>
                {/* Max */}
                <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center">
                    <TextField
                        inputRef={priceMaxRef}
                        id="feed-price-max"
                        type="text"
                        value={values.priceMax || ""}
                        onChange={(e) =>
                            handlePriceInput(
                                e.target.value,
                                (v) => onChange("priceMax", v),
                                dirtyMax,
                                setDirtyMax
                            )
                        }
                        onFocus={() =>
                            handleFocus(
                                priceMaxRef,
                                values.priceMax,
                                (v) => onChange("priceMax", v),
                                setDirtyMax,
                                "priceMax"
                            )
                        }
                        onClick={() =>
                            handleFocus(
                                priceMaxRef,
                                values.priceMax,
                                (v) => onChange("priceMax", v),
                                setDirtyMax,
                                "priceMax"
                            )
                        }
                        onBlur={() => handleBlur("priceMax")}
                        placeholder="$0"
                        size="small"
                        fullWidth
                        error={isInvalid}
                        inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                        }}
                        InputProps={{
                            className: "!tw-px-2",
                            sx: {
                                borderRadius: 2,
                                textAlign: "center",
                                "&.Mui-error": {
                                    color: "red",
                                },
                            },
                            inputProps: {
                                style: {
                                    textAlign: "center",
                                    color: isInvalid ? "red" : "inherit",
                                },
                            },
                        }}
                    />
                    <span className="tw-mt-1 tw-text-xs tw-text-gray-400 tw-text-center">max</span>
                </div>
            </div>
        </div>
    );
};

export default PriceRangeInput;
